import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    fetchCourseDetail();
  }, [id]);

  const fetchCourseDetail = async () => {
    try {
      const response = await axios.get(`http://localhost:5000/api/courses/${id}`);
      setCourse(response.data.course);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course:', error);
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        'http://localhost:5000/api/enrollments',
        { course_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Successfully enrolled in the course!');
    } catch (error) {
      console.error('Enrollment error:', error);
      alert(error.response?.data?.error || 'Enrollment failed. Please try again.');
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('http://localhost:5000/api/leads', {
        course_id: id,
        ...leadData
      });
      setLeadSubmitted(true);
      setShowLeadForm(false);
      alert('Thank you for your interest! We will contact you soon.');
      setLeadData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Lead submission error:', error);
      alert('Failed to submit. Please try again.');
    }
  };

  const handleLeadChange = (e) => {
    setLeadData({
      ...leadData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container text-center py-5">
        <h3>Course not found</h3>
        <a href="/courses" className="btn btn-primary mt-3">Browse Courses</a>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        {/* Left Column - Course Details */}
        <div className="col-lg-8">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item"><a href="/courses">Courses</a></li>
              <li className="breadcrumb-item active" aria-current="page">{course.title}</li>
            </ol>
          </nav>

          <h1 className="mb-3">{course.title}</h1>
          
          <div className="mb-3">
            <span className="badge bg-primary me-2">{course.category_name || 'Uncategorized'}</span>
            <span className={`badge ${course.level === 'beginner' ? 'bg-success' : 
                                   course.level === 'intermediate' ? 'bg-warning' : 'bg-danger'} me-2`}>
              {course.level || 'Beginner'}
            </span>
            <span className="badge bg-info">{course.duration || 'Flexible'}</span>
          </div>

          {course.image && (
            <img 
              src={`http://localhost:5000/uploads/${course.image}`} 
              className="img-fluid rounded mb-4" 
              alt={course.title}
              style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
            />
          )}

          <div className="course-description">
            <h4>About This Course</h4>
            <p>{course.description || 'No description available.'}</p>
          </div>

          {course.short_description && (
            <div className="course-short-desc mt-3">
              <h5>What You'll Learn</h5>
              <p>{course.short_description}</p>
            </div>
          )}

          {/* Reviews Section */}
          <div className="reviews mt-4">
            <h4>Reviews</h4>
            <div className="card">
              <div className="card-body">
                <p className="text-muted">No reviews yet. Be the first to review!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Course Info & Actions */}
        <div className="col-lg-4">
          <div className="card sticky-top" style={{ top: '20px' }}>
            <div className="card-body">
              <h5 className="card-title">Course Information</h5>
              
              <div className="mb-3">
                <strong>Price:</strong>
                {course.discount_price ? (
                  <>
                    <span className="text-decoration-line-through text-muted me-2">
                      ${course.price}
                    </span>
                    <span className="text-success fw-bold">
                      ${course.discount_price}
                    </span>
                  </>
                ) : (
                  <span className="fw-bold">${course.price || 'Free'}</span>
                )}
              </div>

              <div className="mb-2">
                <strong>Level:</strong> {course.level || 'Beginner'}
              </div>

              <div className="mb-2">
                <strong>Duration:</strong> {course.duration || 'Flexible'}
              </div>

              <div className="mb-3">
                <strong>Students:</strong> {course.enrolled_students || 0} enrolled
              </div>

              <hr />

              {/* Action Buttons */}
              <button 
                className="btn btn-primary w-100 mb-2"
                onClick={handleEnroll}
              >
                <i className="bi bi-mortarboard"></i> Enroll Now
              </button>
              
              <button 
                className="btn btn-outline-primary w-100"
                onClick={() => setShowLeadForm(true)}
              >
                <i className="bi bi-envelope"></i> I'm Interested
              </button>

              <hr />

              {/* Share Section */}
              <div className="text-center">
                <small className="text-muted">Share this course</small>
                <div className="mt-2">
                  <button className="btn btn-sm btn-outline-secondary me-1">
                    <i className="bi bi-facebook"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-secondary me-1">
                    <i className="bi bi-twitter"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-secondary me-1">
                    <i className="bi bi-linkedin"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-secondary">
                    <i className="bi bi-whatsapp"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Generation Modal */}
      {showLeadForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Express Interest in {course.title}</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowLeadForm(false)}
                ></button>
              </div>
              <form onSubmit={handleLeadSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      required
                      value={leadData.name}
                      onChange={handleLeadChange}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      required
                      value={leadData.email}
                      onChange={handleLeadChange}
                      placeholder="Enter your email address"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      value={leadData.phone}
                      onChange={handleLeadChange}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-control"
                      name="message"
                      rows="3"
                      value={leadData.message}
                      onChange={handleLeadChange}
                      placeholder="Any specific questions or requirements?"
                    ></textarea>
                  </div>
                  <small className="text-muted">* Required fields</small>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowLeadForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Submit Interest
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {leadSubmitted && (
        <div className="alert alert-success alert-dismissible fade show fixed-top m-3" role="alert" style={{ maxWidth: '500px', margin: 'auto' }}>
          <strong>Thank you!</strong> Your interest has been recorded. We'll contact you soon.
          <button type="button" className="btn-close" onClick={() => setLeadSubmitted(false)}></button>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;